// Booking confirmation. Same .confirm-hero / .pnr-card / .conf-content shell as
// the flight confirmation page.
//
// One thing it gets right that is easy to get wrong: a hotel booking has **two**
// references, and only one of them means anything at the front desk.
//
//   hotel_confirmation_code  → what the PROPERTY knows the stay by. Lead with it.
//   booking_reference        → the supplier's own id. Useful to us and to
//                              support; useless at check-in.
//
// Showing only the supplier reference sends a guest to a desk that has never
// seen it, so both are rendered and labelled for what they are.

import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  fmtBoard, fmtTaxDescription, fmtDateFull, fmtDeadline, fmtMoney, nightsLabel, occupancyLabel, starLabel,
} from "../lib/hotelFormat";
import { KairaMark } from "../components/KairaMark";
import type { BookingListItem } from "../lib/types";
import type { HotelItinerary } from "../lib/hotelTypes";

export function HotelConfirmation() {
  const { bookingId } = useParams();

  useEffect(() => {
    document.body.classList.add("confirm-page");
    return () => document.body.classList.remove("confirm-page");
  }, []);

  const itinQ = useQuery({
    queryKey: ["hotelBooking", bookingId],
    queryFn: () => api.get<HotelItinerary>(`/v1/hotels/booking/${bookingId}/`),
    enabled: !!bookingId,
  });
  const metaQ = useQuery({
    queryKey: ["bookingMeta", bookingId],
    queryFn: () => api.get<BookingListItem>(`/v1/bookings/${bookingId}/`),
    enabled: !!bookingId,
  });

  if (itinQ.isLoading || metaQ.isLoading) {
    return <div className="app-loading"><span className="spinner" />Loading…</div>;
  }
  if (itinQ.error || !itinQ.data || metaQ.error || !metaQ.data) {
    return <div className="app-error">Could not load booking.</div>;
  }

  const b = itinQ.data;
  const meta = metaQ.data;
  const { hotel, room, pricing } = b;
  const currency = pricing.currency;
  const stars = starLabel(hotel.star_rating);
  const issuedAt = new Date(meta.modified_at);
  const leadGuest = b.guests?.find((g) => g.is_lead) || b.guests?.[0];

  return (
    <>
      <section className="confirm-hero">
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 18 }}>
            <KairaMark size={64} />
            <div className="confirm-checkmark" style={{ margin: 0 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
            </div>
          </div>
          <h1>You're <span className="serif">staying</span> at {hotel.name}.</h1>
          <p className="lede">
            {fmtDateFull(b.check_in)} → {fmtDateFull(b.check_out)} · {nightsLabel(b.nights)} ·{" "}
            {occupancyLabel(b.occupancies)}.
            {leadGuest?.email ? <> Confirmation sent to <b>{leadGuest.email}</b>.</> : null}
          </p>

          <div className="pnr-card">
            <div className="col pnr">
              {/* The one the front desk can look up. */}
              <div className="lbl">Hotel confirmation</div>
              <div className="val">
                {b.hotel_confirmation_code || (
                  <span style={{ color: "var(--ink-4)", fontSize: 14 }}>Pending</span>
                )}
              </div>
              {b.hotel_confirmation_code && <CopyChip value={b.hotel_confirmation_code} />}
            </div>
            <div className="col">
              <div className="lbl">Supplier ref</div>
              <div className="val" style={{ fontSize: 18, fontWeight: 700 }}>
                {b.booking_reference || <span style={{ color: "var(--ink-4)", fontSize: 14, fontWeight: 500 }}>—</span>}
              </div>
              {b.booking_reference && <CopyChip value={b.booking_reference} />}
            </div>
            <div className="col">
              <div className="lbl">Booking ID</div>
              <div className="val" style={{ fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, monospace", wordBreak: "break-all", lineHeight: 1.4 }}>
                {b.booking_id}
              </div>
              <CopyChip value={b.booking_id} />
            </div>
            <div className="col">
              <div className="lbl">Confirmed</div>
              <div className="val">{fmtIssuedAt(issuedAt)}</div>
            </div>
          </div>

          <div className="confirm-actions">
            <Link to={`/bookings/${bookingId}`} className="btn btn-primary btn-pill">
              Manage booking
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
            </Link>
            <Link to="/bookings" className="btn btn-ghost btn-pill">All bookings</Link>
            <Link to="/" className="btn btn-ghost btn-pill">Book another stay</Link>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="conf-content">
          <div>
            <div className="itin-card">
              <h3>Your <span className="serif">stay</span></h3>
              <div className="itin-card-sub">
                {stars && <span className="hotel-stars">{stars}</span>}
                {[hotel.address, hotel.city_name].filter(Boolean).join(" · ")}
              </div>

              <div className="hotel-pay-dates" style={{ marginTop: 16 }}>
                <div>
                  <span className="lbl">Check-in</span>
                  <b>{fmtDateFull(b.check_in)}</b>
                  {hotel.check_in_time && <span className="t">from {hotel.check_in_time}</span>}
                </div>
                <div className="hotel-pay-nights">{nightsLabel(b.nights)}</div>
                <div>
                  <span className="lbl">Check-out</span>
                  <b>{fmtDateFull(b.check_out)}</b>
                  {hotel.check_out_time && <span className="t">by {hotel.check_out_time}</span>}
                </div>
              </div>

              <div className="itin-detail" style={{ marginTop: 14 }}>
                <span><b>{room.name}</b> · {fmtBoard(room.board_basis, room.board_name)}</span>
                <span>{occupancyLabel(b.occupancies)}</span>
                <span className={`tag ${room.refundable ? "refundable" : "non-refundable"}`}>
                  {room.refundable ? "Free cancellation" : "Non-refundable"}
                </span>
              </div>

              <div className={`room-cancel${room.refundable ? " ok" : ""}`} style={{ marginTop: 12 }}>
                {b.last_free_cancellation_date
                  ? <>Free cancellation until <b>{fmtDeadline(b.last_free_cancellation_date)}</b></>
                  : room.free_cancellation_until
                  ? <>Free cancellation until <b>{fmtDeadline(room.free_cancellation_until)}</b></>
                  : room.cancellation_summary}
              </div>

              {(hotel.phone || hotel.email) && (
                <div className="hotel-contact">
                  <b>Property contact</b>
                  <span>{[hotel.phone, hotel.email].filter(Boolean).join(" · ")}</span>
                </div>
              )}
            </div>

            <div className="itin-card">
              <h3>On the <span className="serif">booking</span></h3>
              <div className="itin-card-sub">
                One named guest per room, as the property requires. Everyone else in a
                room is listed with them.
              </div>
              {(b.guests || []).map((guest, i) => (
                <div className="passenger-line" key={i}>
                  <div>
                    <b>
                      Room {guest.occupancy_number ?? i + 1} ·{" "}
                      {[guest.title, guest.first_name, guest.last_name].filter(Boolean).join(" ")}
                    </b>
                    <span>
                      {guest.other_occupants?.length
                        ? `with ${guest.other_occupants
                            .map((o) => [o.first_name, o.last_name].filter(Boolean).join(" "))
                            .filter(Boolean).join(", ")}`
                        : "sole named guest"}
                      {guest.is_lead && guest.email ? ` · ${guest.email}` : ""}
                    </span>
                  </div>
                </div>
              ))}
              {!b.guests?.length && (
                <div style={{ color: "var(--ink-3)", fontSize: 13 }}>(No guest data persisted.)</div>
              )}
            </div>
          </div>

          <aside>
            <div className="summary-card">
              <h3>Payment</h3>
              <div className="sub">Wallet debit · {fmtIssuedAt(issuedAt)}</div>
              <div className="fare-line"><span className="lbl">Method</span><span>Partner wallet</span></div>
              <div className="fare-line">
                <span className="lbl">Status</span>
                <span className="status-pill confirmed"><span className="dot" />Captured</span>
              </div>
              <div style={{ height: 1, background: "var(--line)", margin: "14px 0" }} />
              <div className="fare-line">
                <span className="lbl">Room rate · {nightsLabel(b.nights)}</span>
                <span>{fmtMoney(pricing.base_fare, currency)}</span>
              </div>
              <div className="fare-line total">
                <span className="lbl">Paid</span>
                <span>{fmtMoney(pricing.final_fare, currency)}</span>
              </div>
              {meta.wallet_debited_inr && (
                <div className="fare-line" style={{ color: "var(--ink-3)", fontSize: 12 }}>
                  <span className="lbl">Wallet debit (INR)</span>
                  <span>{fmtMoney(parseFloat(meta.wallet_debited_inr), "INR")}</span>
                </div>
              )}

              {!!pricing.taxes_at_property && (
                <>
                  <div className="fare-line at-property-line">
                    <span className="lbl">Payable at the property</span>
                    <span>{fmtMoney(pricing.taxes_at_property, currency)}</span>
                  </div>
                  <ul className="at-property-breakdown">
                    {pricing.taxes_at_property_breakdown.map((tax, i) => (
                      <li key={i}>
                        <span>{fmtTaxDescription(tax.description)}</span>
                        <span>{fmtMoney(tax.amount, tax.currency)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="field-help" style={{ marginTop: 8 }}>
                    Collected by the hotel at check-in — not part of what you paid us.
                  </p>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

function CopyChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }).catch(() => {});
  }
  return (
    <button type="button" className="copy-chip" onClick={copy} aria-label={`Copy ${value}`}>
      {copied ? (
        <>
          <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
          Copied
        </>
      ) : (
        <>
          <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

function fmtIssuedAt(d: Date): string {
  if (Number.isNaN(d.getTime())) return "—";
  return (
    d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) +
    " · " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}
